import React from 'react';
import PropTypes from 'prop-types';
import { withApollo } from '@apollo/react-hoc';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import startCase from 'lodash/startCase';
import get from 'lodash/get';
import findKey from 'lodash/findKey';
import find from 'lodash/find';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import striptags from 'striptags';
import { fromGlobalId } from '../../utils/apollo';
import Icon from '../../components/Icon';
import Dropdown from '../../components/Dropdown';
import Search from '../../components/Search';
import { withEnabledFeatures } from '../../utils/features';
import getCategoryRenderer from './search-category-renderer';
import { searchCategoryChanged } from './actions';
import searchQuery from './search-query';
import SearchResultRenderer from './search-result-renderer';
import './styles.css';
import * as constants from './constants';
import {
  COMMUNITY_GROUPS_ROUTE_VIEW_PARAM,
  COMMUNITY_INSTITUTIONS_ROUTE_VIEW_PARAM,
  COMMUNITY_PEOPLE_ROUTE_VIEW_PARAM,

} from '../SearchResultsPage/constants';
import { DISABLE_LEGACY_COMMUNITY } from '../Authorization/constants';
import {
  renderHEInstitutionProfileModal,
  renderHSInstitutionProfileModal,
  renderUserProfileModal,
} from '../../utils/counselorCommunityModals';

export const COUNSELOR_COMMUNITY_SEARCH = 'counselorcommunity/search';

const baseSearchOptionsConfig = [
  {
    icon: 'search',
    selectIcon: 'content',
    text: 'All',
    value: constants.SEARCH_ALL,
    placeholder: 'Search for institutions, people and more...',
  },
  {
    icon: 'user',
    text: 'People',
    value: constants.SEARCH_COMMUNITY_PEOPLE,
    placeholder: 'Search for people',
  },
  {
    icon: 'university',
    text: 'Institutions',
    value: constants.SEARCH_INSTITUTIONS,
    placeholder: 'Search for institutions',
  },
];

const legacySearchOptionsConfig = [
  {
    icon: 'comments outline',
    text: 'Groups',
    value: constants.SEARCH_COMMUNITY_GROUPS,
    placeholder: 'Search by groups',
  },
  {
    icon: 'newspaper',
    text: 'Posts',
    value: constants.SEARCH_COMMUNITY_POSTS,
    placeholder: 'Search for posts',
  },
];

export const searchIcon = (searchCategory, searchOptionsConfig) => {
  const anOption = searchOptionsConfig.find((each) => each.value === searchCategory) || {};
  return anOption.selectIcon || anOption.icon;
};

export const searchDescription = (item, category) => {
  if (category === constants.SEARCH_COMMUNITY_PEOPLE) {
    return item.field_user_institution_title;
  }
  if (category === constants.SEARCH_INSTITUTIONS) {
    const state = startCase((item.field_state_short_name || get(item.address, 'field_hs_elna_state_latest') || '').toLowerCase());
    const city = startCase((item.field_city || get(item.address, 'field_hs_elna_city') || '').toLowerCase());
    return `${city}, ${state}`;
  }
  return striptags(item.body_value);
};

export const constructSearchResultsRoutePath = (searchCategory, searchFilter) => {
  let viewParam;
  switch (searchCategory) {
    case constants.SEARCH_ALL:
    case constants.SEARCH_COMMUNITY_PEOPLE: {
      viewParam = COMMUNITY_PEOPLE_ROUTE_VIEW_PARAM;
      break;
    }
    case constants.SEARCH_INSTITUTIONS: {
      viewParam = COMMUNITY_INSTITUTIONS_ROUTE_VIEW_PARAM;
      break;
    }
    case constants.SEARCH_COMMUNITY_GROUPS: {
      viewParam = COMMUNITY_GROUPS_ROUTE_VIEW_PARAM;
      break;
    }
  }
  return viewParam ? `/search-results/${viewParam}?keyword=${searchFilter}` : '';
};

export const getAvatarType = (item) => {
  const typeMap = {
    CommunityPerson: 'person',
    HigherEd: 'he',
    HighSchool: 'hs',
  };
  return typeMap[fromGlobalId(item.id).type] || 'other';
};

export const getAvatarURL = (searchCategory, item) => {
  if (searchCategory === constants.SEARCH_INSTITUTIONS) {
    return item.field_he_institution_id ?
      `https://static.naviance.com/libraries/college-logos/${item.field_he_institution_id}.gif` :
      null;
  } else if (searchCategory === constants.SEARCH_COMMUNITY_PEOPLE) {
    return item.field_profile_picture_file_url || null;
  }
  return null;
};

export const searchPlaceholder = (searchCategory, searchOptionsConfig) => (
  searchCategory !== constants.SEARCH_ALL ?
    (searchOptionsConfig.find((each) => each.value === searchCategory) || {}).placeholder :
    'Search'
);

export const searchCategoryText = (searchCategory, searchOptionsConfig) => (
  (searchOptionsConfig.find((each) => each.value === searchCategory) || {}).text
);

export const searchOptions = (searchOptionsConfig) => searchOptionsConfig
  .filter((item) => item.value !== constants.SEARCH_COMMUNITY_POSTS)
  .map((each) => ({
    icon: each.icon,
    text: each.text,
    value: each.value,
  }));

class SearchBar extends React.PureComponent {
  static propTypes = {
    searchBoxId: PropTypes.string,
    searchCategory: PropTypes.string.isRequired,
    searchCategoryChanged: PropTypes.func.isRequired,
    current: PropTypes.shape({
      viewer: PropTypes.shape({
        userName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    client: PropTypes.shape({
      query: PropTypes.func.isRequired,
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    enabledFeatures: PropTypes.array,
  };

  static defaultProps = {
    searchBoxId: 'global-search-box',
    enabledFeatures: [],
  };

  constructor(props) {
    super(props);
    this.requestSearchDebounce = debounce(this.requestSearch, 400);
    this.state = {
      isLoading: false,
      searchFilter: '',
      selectedSearchIndex: -1,
      showUserModal: false,
      showCollegeModal: false,
      showHighschoolModal: false,
      userId: null,
      collegeId: null,
      highschoolId: null,
      idType: null,
    };
  }

  getSearchOptionsConfig = () => {
    const isDisabledLegacyCommunity = this.props.enabledFeatures.includes(DISABLE_LEGACY_COMMUNITY);
    if (isDisabledLegacyCommunity) return baseSearchOptionsConfig;
    return [...baseSearchOptionsConfig, ...legacySearchOptionsConfig];
  }

  handleSearchChange = (e, { value }) => {
    if (value.length < 2) {
      this.resetComponent();
    } else {
      this.setState({ isLoading: true });
      this.requestSearchDebounce(e, value);
    }
  };

  resetComponent() {
    this.setState({ isLoading: false, results: [], originalResults: [], searchFilter: '' });
    this.requestSearchDebounce.cancel();
  }

  handleSearchCategoryChanged = (e, { name, value }) => {
    this.props.searchCategoryChanged(value);
    // force re-fetch
    if (this.state.searchFilter) {
      this.setState({ isLoading: true });
      this.handleSearchChange(e, { value: this.state.searchFilter });
    }
  };

  pushGoogleAnalyticsEvent = (options) => {
    window.dataLayer.push({
      event: 'trackEvent',
      eventCategory: 'User Engagement',
      userId: this.props.current.viewer.userName,
      ...options,
    });
  }

  handleOnChange = (_, { result }) => {
    const { history } = this.props;
    this.pushGoogleAnalyticsEvent({
      'eventAction': 'search-result-selection',
      'eventLabel': this.state.searchFilter,
      'selected-result-key': result.key,
      'selected-result-title': result.title,
    });

    const isDisabledLegacyCommunity = this.props.enabledFeatures.includes(DISABLE_LEGACY_COMMUNITY);
    const communityUrl = isDisabledLegacyCommunity ? COUNSELOR_COMMUNITY_SEARCH : 'community';
    const originalResult = find(this.state.originalResults, { id: result.key });
    const isCollegeResult = !!originalResult.field_he_institution_id;
    const isHighschoolResult = get(originalResult.general, 'field_hs_na_nces_id') || get(originalResult.naviance, 'field_hs_na_nguid');
    const resultCategory = findKey(this.state.results, (item) => (item.results.includes(result)));
    const { id } = fromGlobalId(result.key);

    const userId = originalResult.communityId;
    const collegeId = originalResult.field_he_institution_id;
    const highschoolId = isDisabledLegacyCommunity ?
      get(originalResult.general, 'field_hs_na_nces_id') || get(originalResult.naviance, 'field_hs_na_nguid') :
      get(originalResult.naviance, 'field_hs_na_nguid') || get(originalResult.general, 'field_hs_na_nces_id');
    const highschoolIdType = get(originalResult.general, 'field_hs_na_nces_id') ? 'NCES_ID' : 'NGUID';
    const idType = collegeId ? 'SCID' : highschoolIdType;

    const peopleEndpoint = isDisabledLegacyCommunity ?
      `/people/${originalResult.communityId}` :
      `/profile/${originalResult.communityId}`;

    const collegeEndpoint = isDisabledLegacyCommunity ?
      `/institutions/colleges/${originalResult.field_he_institution_id}` :
      `/institution-id/${originalResult.field_he_institution_id}`;

    const highschoolEndpoint = isDisabledLegacyCommunity ?
      `/institutions/highschools/${highschoolId}?idType=${idType}` :
      `/institution-hs-id/${highschoolId}`;

    if (resultCategory === constants.SEARCH_COMMUNITY_PEOPLE) {
      if (!isDisabledLegacyCommunity) history.push(`/${communityUrl}${peopleEndpoint}`);
      else this.openUserProfileModal({ userId });
      return;
    }

    if (resultCategory === constants.SEARCH_INSTITUTIONS && isCollegeResult) {
      if (!isDisabledLegacyCommunity) history.push(`/${communityUrl}${collegeEndpoint}`);
      else this.openInstitutionProfileModal({ collegeId, highschoolId, idType });
      return;
    }

    if (resultCategory === constants.SEARCH_INSTITUTIONS && isHighschoolResult) {
      if (!isDisabledLegacyCommunity) history.push(`/${communityUrl}${highschoolEndpoint}`);
      else this.openInstitutionProfileModal({ collegeId, highschoolId, idType });
      return;
    }

    /* istanbul ignore else */
    if (!isDisabledLegacyCommunity && resultCategory === constants.SEARCH_COMMUNITY_GROUPS) {
      history.push(`/${communityUrl}/groups/${id}`);
    }
  };

  requestSearch = async (e, value) => {
    const { client, searchCategory } = this.props;
    this.setState({ searchFilter: value });
    const response = await client.query({
      query: searchQuery,
      variables: {
        category: searchCategory,
        filter: value,
      },
    });
    this.handleSearchResults(get(response, 'data.viewer.searchByCategory') || []);
  };

  handleSearchResults = (searchResults) => {
    const filteredSearchResults = searchResults.filter((item) => item.category !== constants.SEARCH_COMMUNITY_POSTS);
    this.pushGoogleAnalyticsEvent({
      eventAction: 'search',
      eventLabel: this.state.searchFilter,
      resultCategoryCount: filteredSearchResults.length,
      resultCount: filteredSearchResults.reduce((count, item) => count + item.results.length, 0),
    });

    const originalResults = [];
    const results = filteredSearchResults.reduce((obj, item) => {
      obj[item.category] = {
        name: searchCategoryText(item.category, this.getSearchOptionsConfig()),
        results: item.results.map((each) => {
          originalResults.push({ ...each, category_name: searchCategoryText(item.category, this.getSearchOptionsConfig()) });
          return {
            renderer: SearchResultRenderer,
            key: each.id,
            icon: searchIcon(item.category, this.getSearchOptionsConfig()),
            image: getAvatarURL(item.category, each),
            description: searchDescription(each, item.category),
            title: each.name,
            type: getAvatarType(each),
          };
        }),
      };
      return obj;
    }, {});
    this.setState({ isLoading: false, results, originalResults });
  };

  handleKeyDown = (e) => {
    if (e.which === 13) {
      const { state, close } = this.searchDropdown || { state: {}, close: noop };
      if ((state.open && state.selectedIndex < 0) || !state.open) {
        close();
        this.handleHardSearch(e);
      }
    }
  };

  handleHardSearch = () => {
    const { searchFilter } = this.state;
    const { history, searchCategory } = this.props;
    if (searchFilter && searchFilter.length >= 2) {
      const routePath = constructSearchResultsRoutePath(searchCategory, searchFilter);
      this.setState({ isLoading: false });
      history.push(routePath);
    }
  };

  handleViewStaff = (event, institutionName, institutionType, institutionSubType) => {
    event.preventDefault();
    this.props.history.push('/counselorcommunity/search/people?' +
      `viewStaffInstitutionName=${institutionName}&` +
      `viewStaffInstitutionType=${institutionType}&` +
      `viewStaffInstitutionSubType=${institutionSubType}`);
    window.dispatchEvent(new Event('counselor-community-serverless-update-route'));
  }

  closeModals = () => this.setState({
    showUserModal: null,
    showCollegeModal: null,
    showHighschoolModal: null,
    userId: null,
    collegeId: null,
    highschoolId: null,
    idType: null,
  });

  openUserProfileModal = ({ userId }) => {
    this.closeModals();
    this.setState({ showUserModal: true, userId });
  }

  openInstitutionProfileModal = ({ collegeId, highschoolId, idType }) => {
    this.closeModals();
    this.setState({
      showCollegeModal: !!collegeId,
      showHighschoolModal: !!highschoolId,
      collegeId,
      highschoolId,
      idType,
    });
  }

  render() {
    const {
      searchFilter,
      results,
      isLoading,
      selectedSearchIndex,
      showUserModal,
      userId,
      idType,
      collegeId,
      showCollegeModal,
      showHighschoolModal,
      highschoolId,
    } = this.state;
    const { searchBoxId, searchCategory, enabledFeatures } = this.props;
    const isDisabledLegacyCommunity = enabledFeatures.includes(DISABLE_LEGACY_COMMUNITY);

    return (
      <div styleName="searchBarContainerNav" role="search">
        {renderUserProfileModal(showUserModal, userId, this.props, this.closeModals, this.openInstitutionProfileModal)}
        {renderHEInstitutionProfileModal(showCollegeModal, idType, collegeId, this.props, this.handleViewStaff)}
        {renderHSInstitutionProfileModal(showHighschoolModal, this.props, highschoolId, idType, this.closeModals, this.handleViewStaff)}
        <Dropdown
          id={`${searchBoxId}-filter`}
          data-cy={`${searchBoxId}-filter`}
          styleName="searchDropdownNav"
          aria-label="Search Category Selector"
          floating
          inline
          trigger={(
            <Icon
              aria-label={searchCategoryText(searchCategory, this.getSearchOptionsConfig())}
              name={searchIcon(searchCategory, this.getSearchOptionsConfig())}
            />
          )}
          options={searchOptions(this.getSearchOptionsConfig())}
          onChange={this.handleSearchCategoryChanged}
        />
        <Search
          id={searchBoxId}
          className="labeled"
          category
          fluid
          placeholder={searchPlaceholder(searchCategory, this.getSearchOptionsConfig())}
          input={{
            'id': `${searchBoxId}-input`,
            'aria-label': searchPlaceholder(searchCategory, this.getSearchOptionsConfig()),
            'data-cy': `${searchBoxId}-input`,
          }}
          results={results}
          onSearchChange={this.handleSearchChange}
          onResultSelect={this.handleOnChange}
          loading={isLoading}
          showNoResults={!isLoading}
          selectedSearchIndex={selectedSearchIndex}
          minCharacters={2}
          categoryRenderer={getCategoryRenderer(searchFilter, isDisabledLegacyCommunity)}
          ref={(search) => { this.searchDropdown = search; }}
          onKeyDown={this.handleKeyDown}
          icon={{
            name: 'search',
            link: true,
            onClick: this.handleHardSearch,
          }}
        />
        <Link
          to={isDisabledLegacyCommunity ? `/${COUNSELOR_COMMUNITY_SEARCH}/people` : '/search-results/people'}
          styleName="advancedSearch"
          data-cy="advanced-search-link"
        >
          {isDisabledLegacyCommunity ? 'Counselor Community Search' : 'Advanced Search'}
        </Link>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  searchCategory: get(state, 'globalSearch.searchCategory') || constants.SEARCH_ALL,
  current: state.current,
});

export const SearchBarComponent = withRouter(SearchBar);
export const ConnectedSearchBarComponent = connect(mapStateToProps, { searchCategoryChanged })(withApollo(SearchBarComponent));
export default withEnabledFeatures(ConnectedSearchBarComponent);
